import { createReducer } from "reduxsauce";
import Types from "./types";
import { NUMBER } from "../../../constants/app-constants";

export const INITIAL_STATE = {
    content: [],
    metaContent: {
        metaTitle: null,
        metaDescription: null,
        metaKeyWord: null,
        heading: null
    },
    totalCars: 0,
    totalPages: null,
    isSSR: false,
    page: 0,
    isLoading: false,
    error: null,
    config: {},
    shouldReloadList: false,
    personalised: false,
    dynamicContent: {},
    ipDetectedState: "",
    searchPageResult: {},
    loadingSearch: false,
    similarCarsContent: [],
    totalSimilarCars: 0,
    totalSimilarPages: null,
    isSimilarCarLoading: null,
    similarCarPage: -1,
    loadingFaq: false,
    buyWithConfidence: [],
    cmsConfig: {
        isCmsConfigLoading: false,
        data: {},
        error: null
    },
    showClassifiedHandpickedBanner: false,
    carouselData: {
        isCarouselDataLoading: false,
        data: [],
        error: null
    },
    videoGalleryData: {},
    videoGalleryDataLoaded: false,
    seoData: {
        isSeoDataLoaded: false,
        isSeoDataLoading: true,
        data: {},
        error: null
    }
};

export const fetchCarList = (state = INITIAL_STATE) => {
    return {
        ...state,
        isLoading: true
    };
};

export const fetchCarListSuccess = (state = INITIAL_STATE, { data, params }) => {
    return {
        ...state,
        content: params.page === 0 ? data.results : [...state.content, ...data.results],
        metaContent: { ...data.metaContent },
        dynamicContent: params.page === 0 ? { ...data.dynamicContent } : { ...state.dynamicContent },
        totalCars: data.total,
        totalPages: data.totalPages,
        personalised: data.personalised,
        isLoading: false,
        error: null,
        ...((params && params.page >= 0) && { page: params.page }),
        config: data.config,
        ipDetectedState: data.ipDetectedState,
        similarCarsContent: [],
        totalSimilarCars: 0,
        similarCarPage: -1,
        totalSimilarPages: null,
        buyWithConfidence: data.buyWithConfidence || []
    };
};

export const fetchCarListFailure = (state = INITIAL_STATE, { error }) => {
    return {
        ...state,
        error,
        isLoading: false
    };
};

export const updateSSRStatus = (state = INITIAL_STATE, {isSSR}) => {
    return {
        ...state,
        isSSR
    };
};

export const resetCarList = (state = INITIAL_STATE) => {
    return {
        ...state,
        content: [],
        totalPages: null
    };
};

export const reloadCarList = (state = INITIAL_STATE, {shouldReloadList}) => {
    return {
        ...state,
        shouldReloadList
    };
};

export const fetchSimilarCarList = (state = INITIAL_STATE) => {
    return {
        ...state,
        isSimilarCarLoading: true
    };
};
export const fetchSimilarCarFailure = (state = INITIAL_STATE, { error }) => {
    return {
        ...state,
        error,
        isSimilarCarLoading: false
    };
};

export const fetchSimilarCarSuccess = (state = INITIAL_STATE, { data, params }) => {
    return {
        ...state,
        totalSimilarCars: data.total,
        totalSimilarPages: Math.ceil(data.total / NUMBER.TWENTY),
        similarCarsContent: [...state.similarCarsContent, ...data.results || []],
        personalised: data.personalised,
        buyWithConfidence: data.buyWithConfidence || [],
        ...(params && params.page >= 0 && { similarCarPage: params.page }),
        isSimilarCarLoading: false,
        error: null,
        config: data.config,
        ipDetectedState: data.ipDetectedState
    };
};

export const cmsConfigInit = (state = INITIAL_STATE) => {
    return {
        ...state,
        isCmsConfigLoading: true
    };
};

export const cmsConfigSuccess = (state = INITIAL_STATE, { data }) => {
    return {
        ...state,
        cmsConfig: data,
        isCmsConfigLoading: false
    };
};

export const cmsConfigFailure = (state = INITIAL_STATE, { error }) => {
    return {
        ...state,
        error,
        isCmsConfigLoading: false
    };
};

export const updateClassifiedHandpickedBannerState = (state = INITIAL_STATE, { state: showClassifiedHandpickedBanner }) => {
    return {
        ...state,
        showClassifiedHandpickedBanner
    };
};

export const fetchCarouselDataInit = (state = INITIAL_STATE) => {
    return {
        ...state,
        isCarouselDataLoading: true
    };
};

export const fetchCarouselDataSuccess = (state = INITIAL_STATE, { data }) => {
    const {data: carouselData = []} = data || {};
    return {
        ...state,
        carouselData: {
            ...state.carouselData,
            data: carouselData,
            isCarouselDataLoading: false
        }
    };
};

export const fetchCarouselDataFailure = (state = INITIAL_STATE, { error }) => {
    return {
        ...state,
        carouselData: {
            ...state.carouselData,
            error,
            isCarouselDataLoading: false
        }
    };
};

export const fetchSEODataInit = (state = INITIAL_STATE) => {
    return {
        ...state,
        isSeoDataLoading: true
    };
};

export const fetchSEODataSuccess = (state = INITIAL_STATE, { data }) => {
    return {
        ...state,
        seoData: {
            data,
            isSeoDataLoading: false,
            isSeoDataLoaded: true,
            error: null
        }
    };
};

export const fetchSEODataFailure = (state = INITIAL_STATE, { error }) => {
    return {
        ...state,
        error,
        seoData: {
            // eslint-disable-next-line key-spacing, comma-spacing
            data: {},
            isSeoDataLoading: false,
            isError: true
        }
    };
};

export const HANDLERS = {
    [Types.FETCH_CAR_LIST]: fetchCarList,
    [Types.FETCH_CAR_LIST_SUCCESS]: fetchCarListSuccess,
    [Types.FETCH_SIMILAR_CAR_LIST_SUCCESS]: fetchSimilarCarSuccess,
    [Types.FETCH_CAR_LIST_FAILURE]: fetchCarListFailure,
    [Types.UPDATE_SSR_STATUS]: updateSSRStatus,
    [Types.RESET_CAR_LIST]: resetCarList,
    [Types.RELOAD_CAR_LIST]: reloadCarList,
    [Types.CMS_CONFIG_INIT]: cmsConfigInit,
    [Types.CMS_CONFIG_SUCCESS]: cmsConfigSuccess,
    [Types.CMS_CONFIG_FAILURE]: cmsConfigFailure,
    [Types.UPDATE_CLASSIFIED_HANDPICKED_BANNER_STATE]: updateClassifiedHandpickedBannerState,
    [Types.FETCH_CAROUSEL_DATA]: fetchCarouselDataInit,
    [Types.FETCH_CAROUSEL_DATA_SUCCESS]: fetchCarouselDataSuccess,
    [Types.FETCH_CAROUSEL_DATA_FAILURE]: fetchCarouselDataFailure,
    [Types.FETCH_SEO_DATA]: fetchSEODataInit,
    [Types.FETCH_SEO_DATA_SUCCESS]: fetchSEODataSuccess,
    [Types.FETCH_SEO_DATA_FAILURE]: fetchSEODataFailure
};

export default createReducer(INITIAL_STATE, HANDLERS);
